import { CONSTANTS } from 'config/constants';
import { convertToSwedishMaterialName } from 'utils/material-enum-to-swedish';
import { AvailableConstructionProperties, ConstructionProperty } from 'models/property';
import { OrderForm, OrderFormRestorationJaw, RestorationData, RestorationTeethRange } from 'models/order-form';
import { LabConstuction } from 'models/order-input/lab-order';
import { emptyConstruction } from './constructions-helper';

const { UPPER_JAW, LOWER_JAW, MATERIALS } = CONSTANTS;

export const extractConstructions = (orderForm: OrderForm) => {
    if (orderForm.restorationData.length > 0) {
        return orderForm.restorationData.map((restoration) => {
            const { material, otherMaterial } = extractMaterial(restoration.material);
            return {
                teethNumbers: createTeethStructure(restoration).teethNumbers,
                teethState: createTeethStructure(restoration).teethState,
                actions: [],
                materials: [],
                warranty: restoration.isWarranty,
                isTioTandvard: restoration.isTioTandvard || false,
                warrantyType: restoration.isWarranty ? 'UNKNOWN' : '',
                properties: {
                    color: restoration.color,
                    material: convertToSwedishMaterialName(material),
                    otherMaterial: convertToSwedishMaterialName(otherMaterial),
                } as ConstructionProperty,
                usedProperties: {
                    MATERIAL: !!material || !!otherMaterial,
                } as AvailableConstructionProperties,
                isMaterialGridValid: true,
                isActionGridValid: true,
            } as LabConstuction;
        });
    }
    return [emptyConstruction()];
};

export const extractDentistSearchText = (orderForm: OrderForm): string => {
    let searchText = '';
    if (orderForm.formFields?.dentistFirstName?.value) {
        searchText += orderForm.formFields.dentistFirstName.value;
    }
    if (orderForm.formFields?.dentistLastName?.value) {
        searchText += ` ${orderForm.formFields.dentistLastName.value}`;
    }
    return searchText;
};

const createTeethStructure = (
    restoration: RestorationData,
): { teethNumbers: number[]; teethState: Map<number, number> } => {
    const structure = new Map<number, number>();
    restoration.teeth?.forEach((tooth) => {
        structure.set(Number(tooth), 1);
    });
    restoration.teethRange?.forEach((range) => {
        const teeth = teethRangeToList(range);
        teeth.forEach((tooth) => {
            structure.set(Number(tooth), 1);
        });
    });
    if (
        restoration.jaw &&
        (!restoration.teeth || restoration.teeth.length === 0) &&
        (!restoration.teethRange || restoration.teethRange.length === 0)
    ) {
        switch (restoration.jaw) {
            case OrderFormRestorationJaw.UPPER_JAW:
                UPPER_JAW.forEach((tooth) => {
                    structure.set(Number(tooth), 1);
                });
                break;
            case OrderFormRestorationJaw.LOWER_JAW:
                LOWER_JAW.forEach((tooth) => {
                    structure.set(Number(tooth), 1);
                });
                break;
            case OrderFormRestorationJaw.BOTH_JAWS:
                LOWER_JAW.forEach((tooth) => {
                    structure.set(Number(tooth), 1);
                });
                UPPER_JAW.forEach((tooth) => {
                    structure.set(Number(tooth), 1);
                });
                break;
            default:
                break;
        }
    }
    const teethNumbers = structure.size > 0 ? Array.from(structure.keys()) : [];
    return {
        teethState: structure,
        teethNumbers,
    };
};

const teethRangeToList = (range: RestorationTeethRange) => {
    if (UPPER_JAW.includes(range.from) && UPPER_JAW.includes(range.to)) {
        const startIndex = UPPER_JAW.indexOf(range.from);
        const endIndex = UPPER_JAW.indexOf(range.to);
        if (startIndex !== -1 && endIndex !== -1) {
            return extractTeethList(UPPER_JAW, startIndex, endIndex);
        }
    } else if (LOWER_JAW.includes(range.from) && LOWER_JAW.includes(range.to)) {
        const startIndex = LOWER_JAW.indexOf(range.from);
        const endIndex = LOWER_JAW.indexOf(range.to);
        if (startIndex !== -1 && endIndex !== -1) {
            return extractTeethList(LOWER_JAW, startIndex, endIndex);
        }
    }
    return [];
};

const extractTeethList = (teeth: string[], startIndex: number, endIndex: number) => {
    const foundTeeth: string[] = [];
    if (startIndex < endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
            foundTeeth.push(teeth[i]);
        }
    } else {
        for (let i = endIndex; i <= startIndex; i++) {
            foundTeeth.push(teeth[i]);
        }
    }
    return foundTeeth;
};

const extractMaterial = (
    material: string | undefined,
): {
    material: string | undefined;
    otherMaterial: string | undefined;
} => {
    if (material) {
        return MATERIALS.includes(material)
            ? { material, otherMaterial: undefined }
            : { material: undefined, otherMaterial: material };
    }

    return { material: undefined, otherMaterial: undefined };
};
